/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2021 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import { UPDATED_SETTINGS } from '@nebula/airglow-plugin-unified-shell/dist/actions';
import { append, assoc, find, map, pipe, propEq, reject, replace, toLower, when } from 'ramda';
import { call, put, select, take, takeLatest } from 'redux-saga/effects';
import {
  writeWorkspaceToOrg,
  writeWorkspaceToUser
} from '../../common/actions';
import {
  selectActiveWorkspace,
  selectOrgWorkspaceSettings,
  selectUserWorkspaceSettings,
  selectWorkspaces
} from '../../common/selectors';
import { CREATE_WORKSPACE, DELETE_WORKSPACE, UPDATE_WORKSPACE, selectWorkspace } from '../actions';
import {
  error,
  newEnabledViewPlugins,
  newSettingsValidation,
  saveToOrg,
  saving,
  selectedWorkspace,
  workspaceName
} from '../data/prefabs';
import { selectSelectedWorkspace } from '../selectors';

export function* handleSaveWorkspace({ type }) {
  yield put(saving.turnOnAction);
  yield put(error.turnOffAction);
  const workspaceSelection = yield select(selectSelectedWorkspace);
  const handler = type === DELETE_WORKSPACE ? handleDeleteWorkspace
    : type === CREATE_WORKSPACE ? handleCreateWorkspace : handleUpdateWorkspace;

  try {
    const updatedActions = yield call(handler, workspaceSelection);

    yield take(UPDATED_SETTINGS);

    yield put(newEnabledViewPlugins.resetAction);
    yield put(newSettingsValidation.resetAction);

    for (let i = 0; i < updatedActions.length; i++) {
      yield put(updatedActions[i]());
    }
  } catch (e) {
    yield put(error.turnOnAction);
  }

  yield put(saving.turnOffAction);
}

const getWorkspaceSaveSettings = workspace => (workspace.type === 'org'
  ? { action: writeWorkspaceToOrg, selector: selectOrgWorkspaceSettings }
  : { action: writeWorkspaceToUser, selector: selectUserWorkspaceSettings });

export function* handleCreateWorkspace() {
  const name = yield select(workspaceName.value);
  const isOrgWorkspace = yield select(saveToOrg.isOn);
  const newWorkspace = {
    id: pipe(replace(/\s+/g, '-'), toLower)(name),
    name,
    type: isOrgWorkspace ? 'org' : 'user'
  };

  const createSettings = getWorkspaceSaveSettings(newWorkspace);
  const workspaces = yield select(createSettings.selector);
  const allWorkspaces = yield select(selectWorkspaces);

  if (!newWorkspace.name || find(propEq('id', newWorkspace.id), allWorkspaces) || newWorkspace.id === 'new') {
    throw new Error('Workspace names must be unique.');
  }

  const enabledViewPlugins = yield select(newEnabledViewPlugins.value);
  const validationPlugins = yield select(newSettingsValidation.value);
  const updatedWorkspaces = append(
    { ...newWorkspace, enabledViewPlugins, validationPlugins },
    workspaces
  );

  yield put(createSettings.action(updatedWorkspaces));

  return [selectedWorkspace(newWorkspace.id)];
}

export function* handleDeleteWorkspace(deleteWorkspace) {
  const deleteSettings = getWorkspaceSaveSettings(deleteWorkspace);

  const workspaces = yield select(deleteSettings.selector);
  const updatedWorkspaces = reject(propEq('id', deleteWorkspace.id), workspaces);

  yield put(deleteSettings.action(updatedWorkspaces));

  return [];
}

export function* handleUpdateWorkspace(updateWorkspace) {
  const updateSettings = getWorkspaceSaveSettings(updateWorkspace);
  const workspaces = yield select(updateSettings.selector);

  const enabledViewPlugins = yield select(newEnabledViewPlugins.value);
  const validationPlugins = yield select(newSettingsValidation.value);
  const updatedWorkspaces = map(
    when(propEq('id', updateWorkspace.id), pipe(
      assoc('enabledViewPlugins', enabledViewPlugins),
      assoc('validationPlugins', validationPlugins)
    )),
    workspaces
  );

  yield put(updateSettings.action(updatedWorkspaces));

  const activeWorkspace = yield select(selectActiveWorkspace);
  return activeWorkspace.id === updateWorkspace.id ? [selectWorkspace] : [];
}

export function* watchCreateWorkspace() {
  yield takeLatest(CREATE_WORKSPACE, handleSaveWorkspace);
}

export function* watchDeleteWorkspace() {
  yield takeLatest(DELETE_WORKSPACE, handleSaveWorkspace);
}

export function* watchUpdateWorkspace() {
  yield takeLatest(UPDATE_WORKSPACE, handleSaveWorkspace);
}
